import { ChainType } from '@polaris/core/models'
import { runnerSessionDataAtom } from '@polaris/core/stores/mpc/atom'
import { atomEffect } from 'jotai-effect'
import { log } from '@/src/services/logger'
import { walletKeyringAtom } from '../wallets/atoms'

/**
 * We use this variable as a semaphore to avoid making too many requests if addresses are not changed
 */
let previousAddresses: Record<number, string> | undefined

/**
 * Track ongoing session generation requests to prevent race conditions and infinite retries
 */
let ongoingRequest: Promise<void> | undefined

function areRecordsEqual<T extends Record<number, string>>(
  a: T | undefined,
  b: T | undefined,
): boolean {
  if (!a || !b) return false
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    if (a[Number(key)] !== b[Number(key)]) return false
  }
  return true
}

/**
 * We need to subscribe to wallet keyring changes to register new auth methods if user change their wallet,
 * or try to regenerate session sig if it's expired
 */
export const mpcSideEffectsAtom = atomEffect((get) => {
  const walletKeyring = get(walletKeyringAtom)
  const mpcSessionData = get(runnerSessionDataAtom)
  const currentAddresses = walletKeyring.addresses
  const equal = areRecordsEqual(currentAddresses, previousAddresses)

  if (walletKeyring && mpcSessionData && !equal) {
    // If there's already an ongoing request, don't start a new one
    if (ongoingRequest) {
      log({
        message:
          'Session generation already in progress, skipping duplicate request',
        context: {
          previousAddresses: Object.keys(previousAddresses || {}).length,
          currentAddresses: Object.keys(currentAddresses || {}).length,
        },
        level: 'info',
      })
      return
    }

    // Log BEFORE attempting session generation
    log({
      message: 'Wallet addresses changed, regenerating MPC session sigs',
      context: {
        previousAddresses: Object.keys(previousAddresses || {}).length,
        currentAddresses: Object.keys(currentAddresses || {}).length,
      },
      level: 'info',
    })

    // Update previousAddresses immediately to prevent infinite retries
    // This acts as a "circuit breaker" - we'll only retry if addresses change again
    previousAddresses = currentAddresses

    // Determine the chain type to use for session generation
    // Use the first available chain type from current addresses
    const availableChainTypes = Object.keys(currentAddresses || {}).map(Number)
    const chainType =
      availableChainTypes.length > 0 ? availableChainTypes[0] : ChainType.Evm

    // Track the ongoing request to prevent race conditions
    ongoingRequest = walletKeyring
      .generateSessionSigs(chainType)
      .then(() => {
        log({
          message:
            'Successfully regenerated MPC session sigs after wallet change',
          context: {
            addresses: Object.keys(currentAddresses || {}).length,
          },
          level: 'info',
        })
      })
      .catch((error) => {
        log({
          message: 'Failed to regenerate MPC session sigs after wallet change',
          context: {
            error: error instanceof Error ? error.message : String(error),
            addresses: walletKeyring.addresses,
          },
          level: 'error',
          error: error instanceof Error ? error : undefined,
        })
      })
      .finally(() => {
        // Clear the ongoing request when done (success or failure)
        ongoingRequest = undefined
      })
  }
})
